import { useCallback, useState } from 'react'
import { BooleanParam, StringParam, useQueryParams } from 'use-query-params'
import { postMessageToMobileApp } from 'utils/mobileApp'

export interface ISendSMSVerificationCodeProps {
  /**
   * unformatted phone number string
   * @example '1112223333'
   */
  cellPhone: string
}

export interface IUseSendSMSVerificationCodeResult {
  /**
   * Indicates if this instance of rogers hosted by the mobile app in a react-native webview.
   */
  mobileApp?: boolean | null
  /**
   * An alert message provided by the hosting app.
   * @example 'Sorry, we didn't recognize that phone number.'
   */
  alertMessage?: string | null
  /**
   * resets alertMessage and success
   */
  reset: () => void
  /**
   * True when the sms code was sent to the provided phone
   */
  success?: boolean | null
  /**
   * Requests an sms confirmation message be sent to the provided phone number
   */
  sendNewCode: (params: ISendSMSVerificationCodeProps) => void
}

/**
 * Posts a message to the hosting react-native requesting an verification via SMS message
 * Expects the hosting app to set url query parameters alertMessage and/or success
 */
export const useSendSMSVerificationCode = () => {
  const [progress, setProgress] = useState<boolean>(false)

  const [{ alertMessage, mobileApp, success }, setQueryParams] = useQueryParams({
    mobileApp: BooleanParam,
    alertMessage: StringParam,
    success: BooleanParam,
  })

  const reset = useCallback(() => {
    setProgress(false)
    setQueryParams({ success: undefined, alertMessage: undefined })
  }, [setQueryParams])

  const sendNewCode = useCallback(
    ({ cellPhone }: ISendSMSVerificationCodeProps) => {
      reset()
      setProgress(true)
      postMessageToMobileApp(!!mobileApp, 'SEND_VERIFICATION_SMS', cellPhone)
    },
    [mobileApp, reset]
  )

  return {
    alertMessage,
    mobileApp,
    reset,
    success,
    sendNewCode,
    progress,
  }
}
